import { useEffect, useRef } from "react";
import useIntersection from "@shared/hooks/useIntersection";
import { HeaderItems } from "@modules/course/non-access/header";
import { useAppContext } from "@context/app";
import {
  separateMoneyNumbers,
  toFarsiNumber,
  toFarsiNumberStrict,
} from "@utils/digits";
import { useScroll } from "@modules/course/non-access/hook/useScroll";
import { COURSE_TYPE, STATUS } from "@types";
import { useAccessContext } from "../context";
import FilePdfIcon from "@svg/course/filePdf";
import GearIcon from "@svg/course/gear";
import NoteBookIcon from "@svg/course/notebook";
import ExamIcon from "@svg/course/exam";
import FileVideo from "@svg/course/fileVideo";
import QaIcon from "@svg/course/qa";
import MedalIcon from "@svg/course/medal";
import { useLayoutContext } from "@context/layout";

export const useNonAccess = () => {
  const { courseData, instructorProduct, comments } = useAccessContext();

  const {
    product: {
      status,
      is_free: isFree,
      prerequisites,
      faqs,
      attribute_type,
      preview,
      description_data,
    },
    lessons,
  } = courseData;
  const { showFestivalBanner } = useAppContext();

  const layoutData = useLayoutContext();
  const { isAdmin } = layoutData;
  const titleRef = useRef<HTMLHeadingElement | null>(null);
  const breadcrumbRef = useRef<HTMLDivElement | null>(null);
  const refs = useRef<{ e: HTMLElement | null; offset: number }[]>([
    { e: null, offset: 0 },
    { e: null, offset: 0 },
    { e: null, offset: 0 },
    { e: null, offset: 0 },
  ]);
  const spyOffsetRef = useRef(0);
  const offsetHeaderItems = isAdmin ? 50 : 0;
  const isPublished = status === STATUS.PUBLISHED;
  const hasPreview = isPublished && !!lessons?.length;
  const showDemo = preview?.show_demo;
  const { visibleTarget } = useIntersection(refs, 1, spyOffsetRef);
  const { scrolled } = useScroll(titleRef, breadcrumbRef);

  const isPack = false; //!!courseData.data.huge_products?.length;
  const hasLongHeader = showFestivalBanner && !scrolled;
  const coursePre = prerequisites.filter((f) => f.type === "course" && f.title);
  const swPre = prerequisites.filter((f) => f.type === "software" && f.title);
  const hasPrequisite = Boolean(swPre.length || coursePre.length);

  const featureLabel =
    attribute_type === COURSE_TYPE.SERVICES ? "مشخصات" : "مشخصات آموزش";

  const prerequisitesSections = [
    {
      title: "پیش‌نیاز",
      items: coursePre,
    },
    {
      title: "نرم‌افزارها",
      items: swPre,
    },
  ];

  const otherProductSection = [
    {
      title: "دیگر آموزش‌های این مدرس",
      items: instructorProduct || [],
    },
  ];

  useEffect(() => {
    setTimeout(() => {
      window.scrollTo({
        top: 0,
        behavior: "smooth",
      });
    }, 100);
  }, []);

  const {
    product: {
      duration,
      attribute_type: type,
      examCount,
      practiceCount,
      course_content: { has_file, has_pdf },
      hasCertificate,
    },
  } = courseData;

  ///set title of video time
  const farsiDuration =
    duration.in_minutes >= 120 ? duration.rounded_in_hour : duration.in_minutes;

  const durationLabel = duration.in_minutes >= 120 ? "ساعت" : "دقیقه";
  const durationTitle =
    type === COURSE_TYPE.COURSE ? "آموزش ویدئویی" : "پادکست صوتی";

  const videoDescription = `${toFarsiNumber(
    farsiDuration
  )} ${durationLabel} ${durationTitle}`;

  const formattedExamCount = toFarsiNumber(examCount);
  const formattedPracticeCount =
    practiceCount >= 10
      ? `+${toFarsiNumber(practiceCount - (practiceCount % 10))}`
      : `${toFarsiNumber(practiceCount)} `;

  const courseContentItem = [
    {
      title: videoDescription,
      icon: (size: number) => <FileVideo width={size} height={size} />,
      enable: Number(duration.hours) || Number(duration.minutes),
    },
    {
      title: `${formattedPracticeCount} تمرین به‌همراه پاسخ`,
      icon: (size: number) => <NoteBookIcon width={size} height={size} />,
      enable: practiceCount,
    },
    {
      title: ` ${formattedExamCount} سوال سنجش یادگیری`,
      icon: (size: number) => <ExamIcon width={size} height={size} />,
      enable: examCount,
    },
    {
      title: "فایل برنامه‌ها و پروژه‌ها",
      icon: (size: number) => <GearIcon width={size} height={size} />,
      enable: has_file,
    },
    {
      title: "فایل PDF یادداشت‌ها و اسلایدها",
      icon: (size: number) => <FilePdfIcon width={size} height={size} />,
      enable: has_pdf,
    },
    {
      title: "تالار پرسش‌ و ‌پاسخ",
      icon: (size: number) => <QaIcon width={size} height={size} />,
      enable: true,
    },
    {
      title: "آزمون و گواهینامه دو زبانه",
      icon: (size: number) => <MedalIcon width={size} height={size} />,
      enable: hasCertificate,
    },
  ];
  const shouldRenderContent =
    courseContentItem.filter((d) => d.enable).length >= 2;
  const hasDescriptionList = Boolean(description_data?.list?.length);
  const shouldRenderHeadline = Boolean(
    !showDemo && preview?.collections?.length
  );
  const shouldRenderPreview = Boolean(
    !shouldRenderHeadline && showDemo && hasPreview && !isFree
  );

  const enablePreview =
    shouldRenderHeadline || shouldRenderContent || hasDescriptionList;

  const headerItems: HeaderItems = {
    preview: {
      id: "preview",
      enable: enablePreview,
      title: shouldRenderPreview ? "پیش‌نمایش" : "سرفصل‌ها",
      showHeader: enablePreview,
      offset: offsetHeaderItems,
    },
    content: {
      id: "content",
      enable: shouldRenderContent,
      title: "محتوای آموزش",
      showHeader: shouldRenderContent,
      offset: offsetHeaderItems,
    },
    about: {
      id: "about",
      enable: Boolean(description_data?.description),
      title: "درباره آموزش",
      showHeader: true,
      offset: offsetHeaderItems,
    },
    prerequisite: {
      id: "prerequisite",
      enable: hasPrequisite,
      title: "پیش‌نیاز و نرم‌افزارها",
      showHeader: Boolean(prerequisites?.length),
      offset: offsetHeaderItems,
    },
    aboutInstructor: {
      id: "inst",
      enable: true,
      title: "درباره مدرس",
      showHeader: true,
      offset: offsetHeaderItems,
    },
    features: {
      id: "features",
      enable: true,
      title: featureLabel,
      showHeader: false,
      offset: offsetHeaderItems,
    },
    faqs: {
      id: "faqs",
      enable: true,
      title: "سئوالات رایج",
      showHeader: faqs === null,
      offset: offsetHeaderItems,
    },
    warranty: {
      id: "warranty",
      enable: true,
      title: "",
      showHeader: false,
      offset: offsetHeaderItems,
    },
    comments: {
      id: "comments",
      enable: Boolean(isPublished && comments?.total),
      title: `دیدگاه‌ها (${separateMoneyNumbers(
        toFarsiNumberStrict(comments?.total || 0)
      )})`,
      showHeader: true,
      offset: offsetHeaderItems,
    },
  };

  return {
    headerItems,
    titleRef,
    breadcrumbRef,
    hasLongHeader,
    visibleTarget,
    spyOffsetRef,
    refs,
    isPack,
    isPublished,
    otherProductSection,
    prerequisitesSections,
    hasPreview,
    showDemo,
    courseContentItem,
  };
};
